import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  Input,
  Text,
  Box,
  Flex,
  HStack,
  VStack,
  Icon,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { importNeedFromFile } from "../../../api/needsApi";
import { listClients } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import NeedForm from "./NeedForm";
import { FieldLabel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

// Tokenized two-step indicator shown at the top of the wizard body.
const StepIndicator = ({ step, labels }) => (
  <HStack spacing="8px" mb="16px" align="center">
    {labels.map((label, idx) => {
      const num = idx + 1;
      const isActive = step === num;
      const isDone = step > num;
      return (
        <React.Fragment key={num}>
          <HStack spacing="8px" align="center">
            <Flex
              align="center"
              justify="center"
              boxSize="22px"
              borderRadius="999px"
              fontSize="11px"
              fontWeight="800"
              bg={isActive || isDone ? "portal.primary" : "portal.soft"}
              color={isActive || isDone ? "white" : "portal.muted"}
              borderWidth="1px"
              borderColor={isActive || isDone ? "portal.primary" : "portal.line"}
              transition="all 0.2s"
            >
              {num}
            </Flex>
            <Text
              fontSize="11px"
              textTransform="uppercase"
              fontWeight="800"
              letterSpacing="0.055em"
              color={isActive ? "portal.text" : "portal.muted"}
              sx={{ marginBottom: "0 !important" }}
            >
              {label}
            </Text>
          </HStack>
          {num < labels.length && (
            <Box flex="1" h="1px" bg="portal.line" minW="16px" />
          )}
        </React.Fragment>
      );
    })}
  </HStack>
);

const CreateNeedWizardModal = ({ isOpen, onClose, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isSupportedFile = (f) => {
    if (!f) return false;
    const mime = f.type;
    const name = (f.name || "").toLowerCase();
    if (
      mime === "application/pdf" ||
      mime ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return true;
    }
    if (name.endsWith(".pdf") || name.endsWith(".docx")) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setFile(null);
    setClientId("");
    setClientSearch("");
    setShowClientDropdown(false);
    setExtractedData(null);
    setDragActive(false);
    listClients(
      { page: 1, pageSize: 1000 },
      { buId: selectedBusinessUnitId || undefined }
    )
      .then((res) => setClients(res?.items || []))
      .catch(() => setClients([]));
  }, [isOpen, selectedBusinessUnitId]);

  const filteredClients = clientSearch.trim()
    ? clients.filter((c) =>
        c.name.toLowerCase().includes(clientSearch.toLowerCase())
      )
    : clients;

  const handleClientSelect = useCallback((client) => {
    setClientId(String(client.id));
    setClientSearch(client.name);
    setShowClientDropdown(false);
  }, []);

  const handleFilesSelected = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected && isSupportedFile(selected)) {
      setFile(selected);
    } else if (selected) {
      toast({
        title: t("pilotNeeds.toasts.invalidFileTypeTitle"),
        description: t("pilotNeeds.toasts.invalidFileTypeDescriptionFile"),
        status: "error"
      });
    }
    event.target.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const f = e.dataTransfer.files[0];
      if (isSupportedFile(f)) {
        setFile(f);
      } else {
        toast({
          title: t("pilotNeeds.toasts.invalidFileTypeTitle"),
          description: t("pilotNeeds.toasts.invalidFileTypeDescriptionFile"),
          status: "error"
        });
      }
    }
  };

  const handleContinueWithDocument = async () => {
    if (!file || !clientId) return;
    setIsSubmitting(true);
    try {
      const res = await importNeedFromFile(file, clientId, {
        buId: selectedBusinessUnitId || undefined
      });
      toast({
        title: t("pilotNeeds.toasts.uploadCompleted"),
        status: "success"
      });
      setExtractedData({
        ...(res || {}),
        client_id: res?.client_id ?? clientId,
        clientId: res?.clientId ?? clientId
      });
      setStep(2);
    } catch (e) {
      toast({
        title: t("pilotNeeds.toasts.uploadFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotNeeds.toasts.deleteFailedDescription"),
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueWithoutUpload = () => {
    // Pass the client selected in step 1 (if any) to the NeedForm
    setExtractedData(clientId ? { client_id: clientId } : null);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setExtractedData(null);
    setFile(null);
    setClientId("");
  };

  const handleFormSuccess = (created) => {
    onSuccess?.(created);
    onClose?.();
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setClientId("");
    setExtractedData(null);
    onClose?.();
  };

  const hasFile = Boolean(file);
  const canContinueWithUpload = hasFile && Boolean(clientId);

  const handleContinue = () => {
    if (hasFile && canContinueWithUpload) {
      handleContinueWithDocument();
    } else {
      handleContinueWithoutUpload();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={step === 2 ? "5xl" : "lg"} scrollBehavior="inside">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {step === 1
            ? t("pilotNeeds.createWizard.step1Title")
            : t("pilotNeeds.createWizard.step2Title")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="16px">
          <StepIndicator
            step={step}
            labels={[
              t("pilotNeeds.createWizard.stepIndicator.document", "Document"),
              t("pilotNeeds.createWizard.stepIndicator.details", "Details"),
            ]}
          />
          {step === 1 ? (
            <>
              <Text fontSize="13px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
                {t("pilotNeeds.createWizard.step1Description")}
              </Text>
              <FormControl mb="16px" position="relative">
                <FieldLabel>{t("pilotNeeds.upload.clientLabel")}</FieldLabel>
                <Input
                  size="sm"
                  placeholder={t("pilotNeeds.upload.clientPlaceholder", { defaultValue: "Search for a company..." })}
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientDropdown(true);
                    if (!e.target.value.trim()) setClientId("");
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                  autoComplete="off"
                />
                {showClientDropdown && filteredClients.length > 0 && (
                  <Box
                    position="absolute"
                    zIndex={10}
                    bg="white"
                    border="1px solid"
                    borderColor="portal.line"
                    borderRadius="10px"
                    mt={1}
                    maxH="200px"
                    overflowY="auto"
                    w="100%"
                    boxShadow="0 10px 24px rgba(16,32,51,.12)"
                  >
                    {filteredClients.slice(0, 50).map((c) => (
                      <Box
                        key={c.id}
                        px={3}
                        py={2}
                        cursor="pointer"
                        bg={String(c.id) === clientId ? "portal.active" : "white"}
                        _hover={{ bg: "portal.soft" }}
                        onClick={() => handleClientSelect(c)}
                        fontSize="13px"
                        color="portal.text"
                      >
                        {c.name}
                        {c.sector ? ` — ${c.sector}` : ""}
                      </Box>
                    ))}
                  </Box>
                )}
                {showClientDropdown && clientSearch.trim() && filteredClients.length === 0 && (
                  <Box
                    position="absolute"
                    zIndex={10}
                    bg="white"
                    border="1px solid"
                    borderColor="portal.line"
                    borderRadius="10px"
                    mt={1}
                    px={3}
                    py={2}
                    w="100%"
                    boxShadow="0 10px 24px rgba(16,32,51,.12)"
                    fontSize="13px"
                    color="portal.muted"
                  >
                    {t("pilotNeeds.upload.noCompaniesFound", { defaultValue: "No companies found" })}
                  </Box>
                )}
              </FormControl>
              <FormControl mb="16px">
                <FieldLabel>{t("pilotNeeds.upload.fileLabel")}</FieldLabel>
                {file ? (
                  <Tag size="md" variant="subtle" colorScheme="brand">
                    <TagLabel>{file.name}</TagLabel>
                    <TagCloseButton onClick={() => setFile(null)} />
                  </Tag>
                ) : (
                  <Box
                    border="2px dashed"
                    borderColor={dragActive ? "portal.primary" : "portal.line"}
                    borderRadius="10px"
                    p={6}
                    textAlign="center"
                    minH="160px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg={dragActive ? "portal.active" : "portal.soft"}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    _hover={{ bg: "portal.active", borderColor: "portal.primary" }}
                  >
                    <VStack spacing={2}>
                      <Icon
                        as={FaCloudUploadAlt}
                        boxSize={8}
                        color="portal.primary"
                      />
                      <Text fontSize="13px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotNeeds.upload.dropzoneFile")}
                      </Text>
                      <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotNeeds.upload.acceptedFile")}
                      </Text>
                    </VStack>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFilesSelected}
                      display="none"
                    />
                  </Box>
                )}
              </FormControl>
            </>
          ) : (
            <NeedForm
              initialData={extractedData}
              onSuccess={handleFormSuccess}
              onCancel={handleClose}
              isEdit={false}
              isActive={true}
              showBackButton={true}
              onBack={handleBack}
            />
          )}
        </ModalBody>
        {step === 1 && (
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              {t("pilotNeeds.upload.buttons.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleContinue}
              isDisabled={hasFile && !canContinueWithUpload}
              isLoading={hasFile && isSubmitting}
              minW="200px"
              px={4}
            >
              {hasFile
                ? t("pilotNeeds.createWizard.buttons.continueWithUpload")
                : t("pilotNeeds.createWizard.buttons.continueWithoutUpload")}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CreateNeedWizardModal;
